import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AlertTriangle, UserPlus, Link2, X } from "lucide-react";
import { useState } from "react";
import { Badge } from "@/components/ui/badge";
import type { AccountMatch } from "@/services/AccountMatchingService";

interface AccountNotFoundDialogProps {
  isOpen: boolean;
  accountNumber: string;
  billId: string;
  similarAccounts: AccountMatch[];
  onSendToOnboarding: () => void;
  onUseSimilarAccount: (accountId: string) => void;
  onCancel: () => void;
}

export function AccountNotFoundDialog({
  isOpen,
  accountNumber,
  billId,
  similarAccounts,
  onSendToOnboarding,
  onUseSimilarAccount,
  onCancel
}: AccountNotFoundDialogProps) {
  const [selectedAccountId, setSelectedAccountId] = useState<string>("");

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onCancel()}>
      <DialogContent className="max-w-3xl">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 bg-orange-100 dark:bg-orange-950 rounded-full">
              <AlertTriangle className="h-6 w-6 text-orange-600" />
            </div>
            <div>
              <DialogTitle>Account Not Found</DialogTitle>
              <DialogDescription>
                Account number <span className="font-mono font-semibold">{accountNumber}</span> does not exist in the system
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Option 1: Send to Onboarding */}
          <Card className="border-2 border-primary/20 hover:border-primary/40 transition-colors cursor-pointer" onClick={onSendToOnboarding}>
            <CardHeader>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-primary/10 rounded-lg">
                  <UserPlus className="h-5 w-5 text-primary" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-base">Send to Onboarding</CardTitle>
                  <CardDescription>
                    Create a request for an admin to set up this new account
                  </CardDescription>
                </div>
                <Badge variant="default">Recommended</Badge>
              </div>
            </CardHeader>
            <CardContent>
              <Button className="w-full" onClick={(e) => { e.stopPropagation(); onSendToOnboarding(); }}>
                <UserPlus className="h-4 w-4 mr-2" />
                Create Onboarding Request
              </Button>
            </CardContent>
          </Card>

          {/* Option 2: Use Similar Account (if any found) */}
          {similarAccounts.length > 0 && (
            <Card className="border-2 border-muted hover:border-muted-foreground/20 transition-colors">
              <CardHeader>
                <div className="flex items-start gap-3">
                  <div className="p-2 bg-blue-100 dark:bg-blue-950 rounded-lg">
                    <Link2 className="h-5 w-5 text-blue-600" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-base">Use Similar Account</CardTitle>
                    <CardDescription>
                      We found {similarAccounts.length} similar account{similarAccounts.length > 1 ? 's' : ''} that might match
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                <Select value={selectedAccountId} onValueChange={setSelectedAccountId}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select a similar account" />
                  </SelectTrigger>
                  <SelectContent>
                    {similarAccounts.map((account) => (
                      <SelectItem key={account.id} value={account.id}>
                        <div className="flex items-center gap-2">
                          <span className="font-mono">{account.account_number}</span>
                          <Badge variant="outline" className="text-xs">
                            {Math.round(account.similarity * 100)}% match
                          </Badge>
                          {account.service_type && (
                            <span className="text-xs text-muted-foreground">({account.service_type})</span>
                          )}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button 
                  className="w-full" 
                  variant="outline"
                  disabled={!selectedAccountId}
                  onClick={() => selectedAccountId && onUseSimilarAccount(selectedAccountId)}
                >
                  <Link2 className="h-4 w-4 mr-2" />
                  Use This Account
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Option 3: Cancel and Edit */}
          <Card className="border-2 border-muted hover:border-muted-foreground/20 transition-colors">
            <CardHeader>
              <div className="flex items-start gap-3">
                <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
                  <X className="h-5 w-5 text-gray-600" />
                </div>
                <div className="flex-1">
                  <CardTitle className="text-base">Cancel and Edit</CardTitle>
                  <CardDescription>
                    Go back and correct the account number manually
                  </CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <Button className="w-full" variant="outline" onClick={onCancel}>
                <X className="h-4 w-4 mr-2" />
                Back to Review
              </Button>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}
